import { getCurrentDate, getCurrentDateTime } from '../utils/date';
import dayjs from 'dayjs';
import type {
  FinanceConfig,
  ReceivablePlan,
  Payable,
  Loan,
  Opportunity,
  Task,
  TaskAttachment,
  TaskUser,
  SubTask,
  Supplier,
  Project,
  Schedule,
  Invoice,
  InvoiceStatus,
} from '../types';
import type { TaskSaveParams } from '../services/modules/project/task';
import { buildSchedulesForProjectTask } from '../utils/taskScheduleSync';
import { labelTaskType } from '../utils/taskLabels';
import { message } from '../utils/message';
import { logger } from '../utils/logger';
import { normalizeInvoiceType } from '../utils/invoiceMeta';

export function mapFinanceConfigFromApi(data: Record<string, unknown>): FinanceConfig {
  const tax =
    typeof data.defaultTaxRate === 'number'
      ? data.defaultTaxRate
      : typeof data.taxRate === 'number'
        ? data.taxRate
        : Number(data.defaultTaxRate ?? data.taxRate ?? 0) || 0;

  const safeStrList = (v: unknown): string[] =>
    Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];

  const accounts = Array.isArray(data.companyAccounts)
    ? (data.companyAccounts as FinanceConfig['companyAccounts'])
    : [];

  const finfo = Array.isArray(data.companyFinanceInfo)
    ? (data.companyFinanceInfo as FinanceConfig['companyFinanceInfo'])
    : [];

  return {
    id: String(data.id ?? 'default'),
    currency: typeof data.currency === 'string' ? data.currency : 'CNY',
    incomeCategories: safeStrList(data.incomeCategories),
    expenseCategories: safeStrList(data.expenseCategories),
    defaultTaxRate: tax,
    companyAccounts: accounts,
    companyFinanceInfo: finfo,
    updatedAt: String(data.updatedAt ?? getCurrentDateTime()),
  };
}

const toFiniteNumber = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toOptionalString = (value: unknown): string | undefined => {
  if (value === null || value === undefined) return undefined;
  const text = String(value).trim();
  return text || undefined;
};

const toDateOnly = (value: unknown, fallback = getCurrentDate()): string => {
  const text = toOptionalString(value);
  return text ? text.slice(0, 10) : fallback;
};

const normalizeInvoiceStatus = (status: unknown): InvoiceStatus => {
  const normalized = String(status ?? '').trim().toLowerCase();
  if (normalized === 'draft') return 'draft';
  if (normalized === 'issued') return 'issued';
  if (normalized === 'claimed') return 'claimed';
  if (normalized === 'voided' || normalized === 'void') return 'voided';
  return 'pending';
};

const normalizeTaxRate = (value: unknown): number => {
  const raw = toFiniteNumber(value, 0);
  return raw > 1 ? raw / 100 : raw;
};

export const mapInvoiceVoToInvoice = (row: Record<string, unknown>): Invoice => {
  const amountWithoutTax = toFiniteNumber(row.amountWithoutTax ?? row.amount, 0);
  const taxAmount = toFiniteNumber(row.taxAmount, 0);
  const explicitTotal = Number(row.totalAmount);
  const totalAmount = Number.isFinite(explicitTotal) ? explicitTotal : amountWithoutTax + taxAmount;
  const createdAt = toDateOnly(row.createdAt ?? row.createTime);

  return {
    id: String(row.id ?? ''),
    version: row.version == null ? undefined : Number(row.version),
    invoiceNo: String(row.invoiceNo ?? ''),
    contractId: toOptionalString(row.contractId),
    contractName: toOptionalString(row.contractName),
    projectId: toOptionalString(row.projectId),
    projectName: toOptionalString(row.projectName),
    marketingActivityId: toOptionalString(row.marketingActivityId),
    marketingActivityName: toOptionalString(row.marketingActivityName),
    companyId: toOptionalString(row.companyId),
    companyName: String(row.companyName ?? ''),
    invoiceTargetType: toOptionalString(row.invoiceTargetType) as 'enterprise' | 'person' | undefined,
    invoiceType: normalizeInvoiceType(toOptionalString(row.invoiceType ?? row.type)),
    taxRate: normalizeTaxRate(row.taxRate),
    taxNo: toOptionalString(row.taxNo),
    address: toOptionalString(row.address),
    phone: toOptionalString(row.phone),
    bankName: toOptionalString(row.bankName),
    bankAccount: toOptionalString(row.bankAccount),
    receivableId: toOptionalString(row.receivableId),
    amountWithoutTax,
    taxAmount,
    totalAmount,
    createTime: toOptionalString(row.createTime),
    issuedDate: toDateOnly(row.issuedDate ?? row.invoiceDate ?? row.issueDate, ''),
    status: normalizeInvoiceStatus(row.status),
    claimedDate: toOptionalString(row.claimedDate),
    claimedBy: toOptionalString(row.claimedBy),
    voidReason: toOptionalString(row.voidReason),
    voidDate: toOptionalString(row.voidDate),
    voidedBy: toOptionalString(row.voidedBy),
    remark: toOptionalString(row.remark),
    applicant: String(row.applicant ?? row.createdByName ?? row.createdBy ?? ''),
    createdBy: toOptionalString(row.createdBy),
    files: toOptionalString(row.files),
    sourceType: toOptionalString(row.sourceType),
    sourceId: toOptionalString(row.sourceId),
    createdAt,
    updatedAt: toDateOnly(row.updatedAt ?? row.updateTime, createdAt),
  };
};

export const mapReceivableStatusToUi = (status: unknown): ReceivablePlan['status'] => {
  const raw = String(status ?? '').toLowerCase();
  if (raw === 'completed') return 'paid';
  if (raw === 'partial') return 'partial';
  if (raw === 'overdue') return 'overdue';
  return 'unpaid';
};

export const mapReceivableStatusToApi = (status: unknown): string => {
  const raw = String(status ?? '').toLowerCase();
  if (raw === 'paid') return 'completed';
  if (raw === 'partial') return 'partial';
  if (raw === 'overdue') return 'overdue';
  return 'pending';
};

export const mapReceivableVoToPlan = (row: Record<string, unknown>): ReceivablePlan => {
  const plannedAmount = Number(row.amount ?? row.plannedAmount ?? 0);
  const receivedAmount = Number(row.receivedAmount ?? 0);
  const unpaidAmount = Number(row.pendingAmount ?? row.unpaidAmount ?? (plannedAmount - receivedAmount));
  const periodRaw = Number(row.period ?? 0);
  const planNo = String(row.planNo ?? '');
  let period = Number.isFinite(periodRaw) && periodRaw > 0 ? periodRaw : 1;
  if ((!Number.isFinite(periodRaw) || periodRaw <= 0) && planNo) {
    const matched = planNo.match(/\d+/);
    if (matched) {
      const parsed = Number(matched[0]);
      if (Number.isFinite(parsed) && parsed > 0) period = parsed;
    }
  }
  const dueDate = String(row.dueDate ?? row.plannedDate ?? '').slice(0, 10) || getCurrentDate();
  const createdAt = String(row.createTime ?? row.createdAt ?? '').slice(0, 10) || getCurrentDate();
  const updatedAt = String(row.updateTime ?? row.updatedAt ?? '').slice(0, 10) || getCurrentDate();
  return {
    id: String(row.id ?? ''),
    version: row.version == null ? undefined : Number(row.version),
    contractId: String(row.contractId ?? ''),
    contractName: String(row.contractName ?? ''),
    projectId: row.projectId != null ? String(row.projectId) : undefined,
    projectName: row.projectName != null ? String(row.projectName) : undefined,
    companyId: row.companyId != null ? String(row.companyId) : undefined,
    companyName: String(row.companyName ?? ''),
    period,
    plannedAmount,
    plannedDate: dueDate,
    receivedAmount,
    unpaidAmount: Math.max(0, unpaidAmount),
    status: mapReceivableStatusToUi(row.status),
    overdueDays: row.overdueDays != null ? Number(row.overdueDays) : undefined,
    lastCollectionDate: row.lastCollectionDate != null ? String(row.lastCollectionDate) : undefined,
    lastCollectionResult: row.lastCollectionResult != null ? String(row.lastCollectionResult) : undefined,
    createdAt,
    updatedAt,
  };
};

/** 将接口行或 store 中未映射的应付款归一为 Payable */
export const normalizePayableRow = (row: Payable | Record<string, unknown>): Payable => {
  const r = row as Payable & Record<string, unknown>;
  if (r.vendorName?.trim()) {
    return r;
  }
  return mapPayableVoToPayable(r as Record<string, unknown>);
};

export const mapPayableVoToPayable = (row: Record<string, unknown>): Payable => {
  const amount = Number(row.amount ?? 0);
  const paidAmount = Number(row.paidAmount ?? 0);
  const unpaidAmount = Number(
    row.pendingAmount ?? row.unpaidAmount ?? Math.max(0, amount - paidAmount),
  );
  const rawStatus = String(row.status ?? 'pending');
  let status: Payable['status'] = 'pending';
  if (rawStatus === 'completed' || rawStatus === 'paid') status = 'paid';
  else if (rawStatus === 'partial') status = 'partial';
  else if (rawStatus === 'cancelled') status = 'cancelled';
  const createdAt = String(row.createTime ?? row.createdAt ?? '').slice(0, 10) || getCurrentDate();
  const updatedAt = String(row.updateTime ?? row.updatedAt ?? '').slice(0, 10) || createdAt;
  return {
    id: String(row.id ?? ''),
    version: row.version == null ? undefined : Number(row.version),
    payableNo: String(row.payableNo ?? ''),
    payableType: (row.payableType ?? row.payable_type ?? 'other') as Payable['payableType'],
    businessType: (row.businessType ?? 'project') as Payable['businessType'],
    marketingActivityId: row.marketingActivityId != null ? String(row.marketingActivityId) : undefined,
    marketingActivityName: row.marketingActivityName != null ? String(row.marketingActivityName) : undefined,
    costCategory: row.costCategory != null ? String(row.costCategory) as Payable['costCategory'] : undefined,
    vendorId: row.supplierId != null ? String(row.supplierId) : undefined,
    vendorName: String(row.vendorName ?? row.supplierName ?? ''),
    projectId: row.projectId != null ? String(row.projectId) : undefined,
    projectName: row.projectName != null ? String(row.projectName) : undefined,
    contractContent: String(row.contractContent ?? row.remark ?? ''),
    amount,
    taxRate: Number(row.taxRate ?? 0),
    taxAmount: Number(row.taxAmount ?? 0),
    netAmount: Number(row.netAmount ?? amount),
    invoiceReceived: Boolean(row.invoiceReceived ?? false),
    plannedPayDate: row.dueDate != null ? String(row.dueDate).slice(0, 10) : undefined,
    paidAmount,
    unpaidAmount,
    status,
    paidDate: row.paidDate != null ? String(row.paidDate).slice(0, 10) : undefined,
    bankSerialNo: row.bankSerialNo != null ? String(row.bankSerialNo) : undefined,
    applicant: String(row.applicant ?? row.createBy ?? ''),
    remark: row.remark != null ? String(row.remark) : undefined,
    createdAt,
    updatedAt,
    contractId: row.contractId != null ? String(row.contractId) : undefined,
  };
};

export const mapLoanVoToLoan = (row: Record<string, unknown>): Loan => {
  const amount = Number(row.amount ?? 0);
  const usedAmount = Number(row.usedAmount ?? 0);
  const pendingRaw = row.pendingAmount ?? row.remainingAmount;
  const remainingAmount = pendingRaw != null
    ? Number(pendingRaw)
    : Math.max(0, amount - usedAmount);
  const createdAt = String(row.createTime ?? row.createdAt ?? '').slice(0, 10) || getCurrentDate();
  const updatedAt = String(row.updateTime ?? row.updatedAt ?? '').slice(0, 10) || createdAt;
  const repayPlan = row.repayPlanDate ?? row.expectedRepayDate;
  return {
    id: String(row.id ?? ''),
    version: row.version == null ? undefined : Number(row.version),
    loanNo: String(row.loanNo ?? ''),
    borrower: String(row.borrower ?? row.borrowerName ?? ''),
    department: String(row.department ?? ''),
    amount,
    usedAmount,
    remainingAmount,
    purpose: String(row.purpose ?? ''),
    loanDate: createdAt,
    expectedRepayDate: repayPlan != null ? String(repayPlan).slice(0, 10) : '',
    status: (row.status ?? 'pending') as Loan['status'],
    remark: row.remark != null ? String(row.remark) : undefined,
    createdAt,
    updatedAt,
  };
};

export const mapReceivablePlanToApiPayload = (
  plan: Partial<ReceivablePlan> & { contractId?: string; contractName?: string; companyName?: string; companyId?: string },
): Record<string, unknown> => {
  const plannedAmount = Number(plan.plannedAmount ?? 0);
  const receivedAmount = Number(plan.receivedAmount ?? 0);
  const unpaidAmount = Number(
    plan.unpaidAmount ?? Math.max(0, plannedAmount - receivedAmount),
  );
  const period = Number(plan.period ?? 1) || 1;
  return {
    version: plan.version,
    contractId: plan.contractId,
    contractName: plan.contractName,
    companyId: plan.companyId,
    companyName: plan.companyName,
    planNo: `P${period}`,
    amount: plannedAmount,
    receivedAmount,
    pendingAmount: unpaidAmount,
    dueDate: plan.plannedDate,
    status: mapReceivableStatusToApi(plan.status),
    remark: (plan as { remark?: string }).remark,
  };
};

/** 任务创建/更新后，按任务人拆分为多条项目日程（与 buildSchedulesForProjectTask 约定 id 前缀） */
export type ScheduleSyncGetter = () => {
  projects: Project[];
  schedules: Schedule[];
  addSchedule: (schedule: Schedule, options?: { quiet?: boolean; skipFetch?: boolean }) => Promise<string | null>;
  deleteSchedule: (id: string, options?: { quiet?: boolean }) => Promise<void>;
  fetchSchedules: () => Promise<void>;
};

export async function syncProjectTaskAssigneeSchedules(get: ScheduleSyncGetter, savedTask: Task): Promise<number> {
  if (!String(savedTask.projectId || '').trim()) return 0;
  const fromStore = get().projects.find((p) => p.id === savedTask.projectId);
  const customerId = String(
    savedTask.companyId || fromStore?.companyId || '',
  ).trim();
  const customerName =
    savedTask.companyName || fromStore?.companyName || savedTask.customerName || '';
  const project = {
    id: savedTask.projectId!,
    name: fromStore?.name || savedTask.projectName || '',
    companyId: customerId || undefined,
    companyName: customerName || undefined,
  };
  if (!String(project.id || '').trim()) return 0;

  const stRows = (savedTask.subTasks || []).filter(
    (st) => String(st.userId || '').trim() && st.startDate && st.endDate,
  );
  const fallback = (savedTask.taskUsers || []).map((u) => ({ id: u.id, name: u.name }));
  const schedules = buildSchedulesForProjectTask({
    taskId: savedTask.id,
    title: savedTask.title,
    description: savedTask.description || '',
    taskTypeLabel: labelTaskType(savedTask.type),
    location: savedTask.location || '',
    project,
    subTasks: stRows.map((st) => ({
      userId: st.userId,
      userName: st.userName,
      roleType: st.roleType,
      roleTypeName: st.roleTypeName,
      startDate: st.startDate,
      endDate: st.endDate,
    })),
    fallbackTaskUsers: stRows.length === 0 && fallback.length > 0 ? fallback : undefined,
    taskStartDate: savedTask.startDate,
    taskEndDate: savedTask.endDate || savedTask.dueDate,
  });

  if (schedules.length === 0) {
    message.warning('未同步日程：请至少添加一名任务人并填写起止日期');
    return 0;
  }
  if (!customerId) {
    message.warning('未同步日程：任务缺少关联客户，请重新选择客户与项目后再保存');
    return 0;
  }

  let synced = 0;
  let failed = 0;
  for (const s of schedules) {
    const createdId = await get().addSchedule(s, { quiet: true, skipFetch: true });
    if (createdId) synced += 1;
    else failed += 1;
  }
  if (synced > 0) {
    await get().fetchSchedules();
  }
  if (failed > 0) {
    message.warning(`有 ${failed} 条项目日程同步失败，请确认任务已选客户与项目`);
  }
  return synced;
}

export async function replaceDerivedSchedulesForProjectTask(get: ScheduleSyncGetter, task: Task): Promise<number> {
  const tid = String(task.id || '').trim();
  const prefix = `${tid}_schedule_`;
  await get().fetchSchedules();
  const old = get().schedules.filter(
    (s) => s.linkedTaskId === tid || String(s.id).startsWith(prefix),
  );
  const hasHistoricalCompletedSchedule = old.some((s) => s.status === 'completed');
  if (hasHistoricalCompletedSchedule) {
    return 0;
  }
  for (const s of old) {
    await get().deleteSchedule(s.id, { quiet: true });
  }
  return syncProjectTaskAssigneeSchedules(get, task);
}

/** 备注/失败原因：兼容 remark、loseReason、notes（与后端 VO 对齐） */
export function opportunityNotesFromApiRow(row: Record<string, unknown>): string {
  const candidates = [row.remark, row.loseReason, row.notes];
  for (const c of candidates) {
    if (c != null && String(c).trim() !== '') return String(c).trim();
  }
  return '';
}

export function normalizeOpportunityFromApi(row: Record<string, unknown>): Opportunity {
  const stage = (row.stage as Opportunity['stage']) || 'lead';
  const amountRaw = row.amount;
  const amount = typeof amountRaw === 'number' ? amountRaw : Number(amountRaw) || 0;
  const probRaw = row.probability;
  const probability = typeof probRaw === 'number' ? probRaw : Number(probRaw) || 0;
  const ownerId = row.ownerId != null ? String(row.ownerId) : '';
  const ownerName = row.ownerName != null ? String(row.ownerName) : '';
  const expected =
    (row.expectedDate != null && String(row.expectedDate)) ||
    (row.expectedCloseDate != null && String(row.expectedCloseDate)) ||
    '';
  const ct = row.createTime != null ? String(row.createTime) : '';
  const ut = row.updateTime != null ? String(row.updateTime) : '';
  return {
    id: String(row.id ?? ''),
    companyId: String(row.companyId ?? ''),
    companyName: String(row.companyName ?? ''),
    contactId: row.contactId ? String(row.contactId) : undefined,
    contactName: row.contactName ? String(row.contactName) : undefined,
    contactTeamJson: row.contactTeamJson ? String(row.contactTeamJson) : undefined,
    name: String(row.name ?? ''),
    code: row.code ? String(row.code) : undefined,
    amount,
    stage,
    probability,
    priority: (row.priority as Opportunity['priority']) || undefined,
    startDate: row.startDate ? String(row.startDate) : undefined,
    expectedCloseDate: expected,
    owner: ownerName || ownerId,
    ownerId: ownerId || undefined,
    ownerName: ownerName || undefined,
    status: String(row.stage ?? ''),
    createTime: ct || undefined,
    createdAt: ct,
    updatedAt: ut,
    notes: opportunityNotesFromApiRow(row),
    expectedDate: expected || undefined,
    projectId: row.projectId ? String(row.projectId) : undefined,
    ownerTeamJson:
      row.ownerTeamJson != null && String(row.ownerTeamJson).trim() !== ''
        ? String(row.ownerTeamJson)
        : undefined,
  };
}

/** 后端 TaskVO 字段 name/dueDate 等与前端 Task(title/dueDate) 对齐 */
export function fmtApiDate(v: unknown): string {
  if (v == null || v === '') return '';
  const s = String(v);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

export function normalizeTaskStatusFromApi(s: unknown): Task['status'] {
  const raw = String(s ?? 'pending');
  if (raw === 'cancelled') return 'completed';
  if (raw === 'pending' || raw === 'in_progress' || raw === 'completed' || raw === 'overdue') {
    return raw;
  }
  return 'pending';
}

function parseTaskAttachments(raw: unknown): TaskAttachment[] {
  if (Array.isArray(raw)) {
    return raw as TaskAttachment[];
  }
  if (typeof raw !== 'string' || raw.trim() === '') {
    return [];
  }
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as TaskAttachment[]) : [];
  } catch {
    return [];
  }
}

export function mapApiTaskRecordToTask(row: Record<string, unknown>): Task {
  const tu = (row.taskUsers as Array<Record<string, unknown>> | undefined) ?? [];
  const taskUsers: TaskUser[] = tu.map((u) => ({
    id: String(u.userId ?? u.id ?? ''),
    name: String(u.userName ?? ''),
  }));
  const subTasks: SubTask[] = tu.map((u, idx) => {
    const st = String(u.status ?? 'pending');
    const subSt: SubTask['status'] =
      st === 'in_progress' || st === 'completed' ? st : 'pending';
    return {
      id: String(u.id ?? `${row.id}-${u.userId}-${idx}`),
      userId: String(u.userId ?? ''),
      userName: String(u.userName ?? ''),
      roleType: String(u.roleType ?? ''),
      roleTypeName: String(u.roleTypeName ?? ''),
      description: String(u.description ?? ''),
      startDate: fmtApiDate(u.startDate),
      endDate: fmtApiDate(u.endDate),
      status: subSt,
      amount: u.amount != null && !Number.isNaN(Number(u.amount)) ? Number(u.amount) : undefined,
    };
  });
  const title = String(row.name ?? row.title ?? '');
  const typ = String(row.type ?? 'other');
  const pri = String(row.priority ?? 'medium');
  const attachmentFiles = typeof row.attachmentFiles === 'string' ? row.attachmentFiles : undefined;
  return {
    id: String(row.id ?? ''),
    title,
    description: String(row.description ?? ''),
    type: typ as Task['type'],
    status: normalizeTaskStatusFromApi(row.status),
    priority: (pri === 'high' || pri === 'low' ? pri : 'medium') as Task['priority'],
    dueDate: fmtApiDate(row.dueDate),
    startDate: fmtApiDate(row.startDate),
    endDate: fmtApiDate(row.endDate),
    days: row.days != null ? Number(row.days) : undefined,
    amount: row.amount != null ? Number(row.amount) : undefined,
    amountType: row.amountType as Task['amountType'],
    location: row.location != null ? (Array.isArray(row.location) ? row.location.join(' ') : String(row.location)) : '',
    attachmentFiles,
    attachments: parseTaskAttachments(row.attachments ?? attachmentFiles),
    taskUsers,
    subTasks,
    companyId: row.companyId ? String(row.companyId) : undefined,
    companyName: row.companyName ? String(row.companyName) : undefined,
    projectId: row.projectId ? String(row.projectId) : undefined,
    projectName: row.projectName ? String(row.projectName) : undefined,
    milestoneId: row.milestoneId ? String(row.milestoneId) : undefined,
    scheduleId: row.scheduleId != null ? String(row.scheduleId) : undefined,
    ownerId: row.ownerId != null && String(row.ownerId).trim() !== '' ? String(row.ownerId) : undefined,
    owner: String(row.ownerName ?? row.assigneeName ?? ''),
    createdAt: fmtApiDate(row.createTime) || dayjs().format('YYYY-MM-DD'),
    completedAt: row.actualFinishDate != null ? fmtApiDate(row.actualFinishDate) : undefined,
  };
}

export function taskToSaveParams(task: Task | Partial<Task>, base?: Task): TaskSaveParams {
  const t = { ...base, ...task } as Task;
  if ((t.projectId == null || String(t.projectId).trim() === '') && base?.projectId) {
    t.projectId = base.projectId;
  }

  const taskUsersFromSubTasks = (t.subTasks ?? [])
    .filter((st) => String(st.userId || '').trim() && st.startDate && st.endDate)
    .map((st) => ({
      userId: st.userId,
      userName: st.userName || st.userId,
      roleType: st.roleType,
      roleTypeName: st.roleTypeName,
      description: st.description,
      startDate: st.startDate,
      endDate: st.endDate,
      amount: st.amount,
      status: st.status,
    }));

  const taskUsersFromList = (t.taskUsers ?? [])
    .filter((u) => String(u.id || '').trim())
    .map((u) => ({
      userId: u.id,
      userName: u.name || u.id,
    }));

  return {
    name: t.title || '',
    description: t.description,
    type: t.type,
    status: t.status,
    projectId: t.projectId,
    projectName: t.projectName,
    milestoneId: t.milestoneId,
    companyId: t.companyId,
    companyName: t.companyName,
    ownerName: t.owner,
    assigneeName: t.owner,
    startDate: t.startDate,
    endDate: t.endDate,
    dueDate: t.dueDate,
    days: t.days,
    amount: t.amount,
    amountType: t.amountType,
    location: t.location,
    remark: t.description,
    priority: t.priority,
    scheduleId: t.scheduleId,
    taskUsers: taskUsersFromSubTasks.length > 0 ? taskUsersFromSubTasks : taskUsersFromList,
  };
}

/** 写入财务供应商接口（与后端 SupplierSaveDTO 字段对齐） */
export function supplierToSavePayload(s: Supplier): Record<string, unknown> {
  return {
    id: s.id,
    version: s.version,
    name: s.name,
    supplierNo: s.supplierNo,
    shortName: s.shortName,
    type: s.type,
    category: s.category,
    contactPerson: s.contactPerson,
    contactPhone: s.contactPhone,
    contactEmail: s.contactEmail,
    address: s.address,
    taxNo: s.taxNo,
    bankName: s.bankName,
    bankAccount: s.bankAccount,
    status: s.status,
    cooperationStartDate: s.cooperationStartDate,
    cooperationEndDate: s.cooperationEndDate,
    settlementType: s.settlementType,
    creditPeriod: s.creditPeriod,
    owner: s.owner,
    remark: s.remark,
  };
}

export const handleApiError = (error: unknown, defaultMessage: string) => {
  if (error && typeof error === 'object' && '__CANCELLED__' in error) {
    return;
  }
  logger.error(error);
  if (error instanceof Error && error.message.trim()) {
    message.error(error.message);
    return;
  }
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as { response?: { data?: { msg?: string; message?: string } } };
    const data = axiosError.response?.data;
    message.error(data?.message || data?.msg || defaultMessage);
  } else {
    message.error(defaultMessage);
  }
};
